import { useAnalytics } from "@lazarentertainment/analytics-collector"
import { useChannelContext } from "client/components/channel"
import { useCallback, useEffect } from "react"

export const useVideoAnalyticsEvents = (
  videoElement: HTMLVideoElement | null,
) => {
  const { analytics } = useAnalytics()
  const { channelId, broadcastId, recordingId } = useChannelContext()

  const onPlay = useCallback(() => {
    if (videoElement !== null) {
      analytics.track("play_video", {
        channelId,
        broadcastId: recordingId ? null : broadcastId,
        recordingId,
        videoTimestamp: videoElement.currentTime,
      })
    }
  }, [videoElement, channelId, broadcastId, recordingId])

  const onPause = useCallback(() => {
    if (videoElement !== null) {
      analytics.track("pause_video", {
        channelId,
        broadcastId: recordingId ? null : broadcastId,
        recordingId,
        videoTimestamp: videoElement.currentTime,
      })
    }
  }, [videoElement, channelId, broadcastId, recordingId])

  const onDestroy = useCallback(() => {
    if (videoElement !== null && !videoElement.paused) {
      // Ignore possible current live broadcastId value if watching while VOD while a different show is live
      analytics.track("pause_video", {
        channelId,
        broadcastId: recordingId ? null : broadcastId,
        recordingId,
        videoTimestamp: videoElement.currentTime,
      })
    }
  }, [videoElement, channelId, broadcastId, recordingId])

  useEffect(() => {
    videoElement?.addEventListener("play", onPlay)
    videoElement?.addEventListener("pause", onPause)
    window.addEventListener("pagehide", onDestroy)

    return () => {
      videoElement?.removeEventListener("play", onPlay)
      videoElement?.removeEventListener("pause", onPause)
      window.removeEventListener("pagehide", onDestroy)
    }
  }, [videoElement])

  /* Returns a function to send an analytics event when we are about to destroy the video.
   * We need to call this from the outside because there's no way to listen to
   * the videoElement for when the video is about to be destroyed.
   * We still need to access videoElement.currentTime before destroying, for analytics
   */
  return { sendAnalyticsOnDestroy: onDestroy }
}
